/* =============================================================================
   Component - the base class every ics-* widget extends. It carries the
   guarantees the whole library relies on:
     - IDEMPOTENT INIT: re-initializing an element returns the EXISTING instance,
       so double-binding is structurally impossible and re-scans are safe.
     - DETERMINISTIC OPTIONS: static defaults <- data-* <- ctor argument (options.js).
     - LEAK-FREE TEARDOWN: every listener/timer/observer goes through EventBinder,
       so destroy() can always release everything.
     - UNIFORM EVENTS: emit() fires CustomEvent('ics:<type>') AND the matching
       on<Type> option callback; a programmatic setValue fires change synchronously.
     - RUNTIME OPTION PATCHING: option(name[, value]) is the one reconfigure path.
   Inheritance is exactly two levels: Component -> concrete widget. Shared
   capabilities come from mixins (mixins.js), never from base-class bloat.
   Public surface: option() · emit() · on() · destroy(), the static defaults /
   resolveOptions() / get(el), and the subclass hooks _init() / _optionChanged() /
   _destroy(). Depends on core/event-binder.js and core/options.js.
   ============================================================================= */
import { EventBinder } from './event-binder.js';
import { resolveOptions } from './options.js';

/**
 * @template {object} TOptions
 * @abstract
 */
export class Component {
  /**
   * @param {HTMLElement} el
   * @param {Partial<TOptions>} [options]
   */
  constructor(el, options) {
    const existing = el[Component.KEY];
    if (existing) return existing;                    // idempotent init
    this.el = el;
    /** @type {TOptions} */
    this.options = /** @type {any} */ (this.constructor).resolveOptions(el, options);
    this._events = new EventBinder(el);
    this._disposed = false;
    el[Component.KEY] = this;
    this._init();
  }

  /* CROSS-BUNDLE key (Symbol.for, not Symbol). A page may load more than one
     bundle of this library, and each bundle compiles its own copy of this class.
     With a per-bundle Symbol the constructor's idempotency guard above could not
     see another bundle's instance, so every element would mount TWICE - and the
     second mount would read the first mount's generated chrome as its own label.
     Symbol.for shares one key across every bundle in the realm, so the guard works
     no matter how many copies of the library a page loads. */
  static KEY = Symbol.for('ics.component');

  /** merged defaults; subclasses override */
  static defaults = {};

  /** defaults <- data-* <- ctor argument (single resolution path; options.js) */
  static resolveOptions(el, options) {
    return resolveOptions(this, el, options);
  }

  /** resolve an already-attached instance (the facade and Ics.mount use this) */
  static get(el) {
    return (el && el[Component.KEY]) || null;
  }

  /** @abstract build DOM + bind - subclasses implement */
  _init() {}

  /** re-render hook after option(name, value); subclasses override as needed */
  _optionChanged(_name, _value, _old) {}

  /**
   * Read or patch options at runtime - the single reconfigure path.
   *   option('name')          -> current value
   *   option('name', value)   -> set + _optionChanged hook
   *   option({a:1, b:2})      -> set several at once (each through the hook)
   */
  option(name, value) {
    if (typeof name === 'object') {
      for (const [k, v] of Object.entries(name)) this.option(k, v);
      return this;
    }
    if (arguments.length === 1) return this.options[name];
    const old = this.options[name];
    this.options[name] = value;
    this._optionChanged(name, value, old);
    return this;
  }

  /**
   * Emit a namespaced DOM event AND the matching option callback:
   *   emit('change', v) -> CustomEvent('ics:change', {detail:v}) + options.onChange(v)
   * The callback receives the detail, with `this` bound to the component.
   * The event bubbles and is cancelable; a disposed component emits nothing.
   * @returns {boolean} false if the CustomEvent was defaultPrevented
   */
  emit(type, detail) {
    if (this._disposed) return true;
    const ok = this.el.dispatchEvent(new CustomEvent('ics:' + type, {
      detail, bubbles: true, cancelable: true,
    }));
    const cb = this.options['on' + type.charAt(0).toUpperCase() + type.slice(1)];
    if (typeof cb === 'function') cb.call(this, detail);
    return ok;
  }

  /** subscribe to this component's namespaced events (auto-cleaned on destroy) */
  on(type, fn) {
    this._events.add(this.el, 'ics:' + type, fn);
    return this;
  }

  /** idempotent teardown: unbind everything, drop injected DOM + the instance ref */
  destroy() {
    if (this._disposed) return this;
    this._disposed = true;
    this._destroy();                                  // subclass hook: remove injected DOM
    this._events.removeAll();
    delete this.el[Component.KEY];
    return this;                                      // mutators return `this` so calls chain
  }

  /** @abstract subclass teardown hook (remove injected panels/masks/etc.) */
  _destroy() {}
}
